import { useSessionRecordingStore } from '@heidi/capture';
import { getUserStorageScope } from '@heidi/utils/jotai-storage';
import { type EffectiveSessionTimeout } from '@heidi/workspace/contracts';
import { useCurrentUserQueryResult } from '@heidi/workspace/data';
import { useEffect, useRef } from 'react';

import { type ComplianceNotificationFlagGates } from './types';
import { useHasPinnedComplianceNotifications } from './usePinnedComplianceNotifications';
import { useHasQualifiedSessionEngagement } from './useQualifiedSessionEngagement';

const AUTO_OPEN_SESSION_KEY = 'heidi.meddeviceComplianceNotifications.autoOpened';

/**
 * The latch is per user, not per tab. `sessionStorage` outlives a logout in the
 * same tab, so an unscoped key let the second user of a shared workstation
 * inherit the first user's "already auto-opened" mark and never see the
 * popover. Scoped with the same `_<user_ref_id>` suffix the user-scoped
 * localStorage atoms use, so an account switch gets its own latch.
 *
 * `null` while the id is unresolved (first login, or a cold start before
 * `useCacheUserRefIdForStorage` writes it) — the in-memory ref still holds the
 * once-per-mount guarantee, we just don't persist to an unscoped bucket.
 */
const getAutoOpenLatchKey = (): string | null => {
  const scope = getUserStorageScope();
  return scope ? `${AUTO_OPEN_SESSION_KEY}_${scope}` : null;
};

/**
 * Opens the notifications popover once per browser session after login when
 * the user has crossed the qualified-session engagement threshold and still has
 * at least one pinned compliance recommendation to show.
 *
 * MODAL PRIORITY CHECK
 * Surface: notifications popover auto-opened after login, carrying the pinned
 *          MFA / inactivity-logout compliance recommendations.
 * Tier:    T4 onboarding & education. Format: popover (the tier's own format).
 * Launch?: yes — auto-shown, so in scope for T0–T5.
 *
 * - R1 clinical  ✓ suppressed while a session recording is active.
 * - R2 enterprise ✓ suppressed for managed users.
 * - R3 grace     N/A for T4, and the 6-qualified-session threshold is a
 *                stricter settling gate than T4 asks for.
 * - R4 dismiss   ✓ per notification, in `complianceNotificationDismissAtom`
 *                (device-local — see the marker there). The auto-open itself is
 *                latched, not dismissible.
 * - R5 one/sess  ✓ for this surface (the latch below). ✗ cross-surface: this
 *                popover cannot see whether another T4/T5 already showed, so it
 *                and e.g. the CSAT popover can both fire in one session.
 * - R6 queue+id  ✗ NOT satisfiable in this PR. `MODAL_TIER_REGISTER` is keyed by
 *                `ModalId`, a backend enum reaching the FE through generated
 *                `packages/schema` — there is no value for this surface and the
 *                schema may not be hand-edited, so it can be neither enqueued
 *                nor registered until the enum lands upstream.
 *
 * R5-cross and R6 are the same missing dependency and are tracked together; the
 * register entry is what then buys the cross-surface mutex for free. Until then
 * the interruption is bounded by the guards above rather than by the queue.
 */
export function useAutoOpenComplianceNotificationsOnLogin({
  effectiveSessionTimeout,
  flagGates,
  enabled,
  isNotificationsOpen,
  setNotificationsOpen,
}: {
  /**
   * The app's resolved session timeout, threaded to the pinned-notification
   * hooks behind the `@heidi/growth` door. `useEffectiveSessionTimeout` is not a
   * flag — it writes a user-scoped override and decides what `AutoLogout`
   * enforces — so it stayed in the settings tree and `SideNavFooter` reads it.
   * `null` means the user is outside the session-timeout cohort, which is what
   * makes the inactivity recommendation ineligible.
   */
  effectiveSessionTimeout: EffectiveSessionTimeout | null;
  /**
   * PostHog-resolved rollout gates. Same crossing as the timeout: this package
   * cannot read PostHog, so `SideNavFooter` supplies the booleans.
   */
  flagGates: ComplianceNotificationFlagGates;
  enabled: boolean;
  isNotificationsOpen: boolean;
  setNotificationsOpen: (open: boolean) => void;
}) {
  const { data: isManagedUser } = useCurrentUserQueryResult(
    (data) => data.is_managed ?? false
  );
  const isRecordingActive =
    useSessionRecordingStore((state) => state.status === 'RECORDING') ?? false;
  const hasPinnedNotifications = useHasPinnedComplianceNotifications(
    effectiveSessionTimeout,
    flagGates
  );
  const hasQualifiedEngagement = useHasQualifiedSessionEngagement(
    enabled && hasPinnedNotifications && !isManagedUser && !isRecordingActive
  );
  const hasAttemptedAutoOpenRef = useRef(false);

  useEffect(() => {
    if (
      !enabled ||
      isManagedUser ||
      isRecordingActive ||
      hasAttemptedAutoOpenRef.current
    ) {
      return;
    }
    if (!hasPinnedNotifications || !hasQualifiedEngagement) return;

    const latchKey = typeof window === 'undefined' ? null : getAutoOpenLatchKey();
    if (latchKey) {
      try {
        if (sessionStorage.getItem(latchKey) === '1') {
          hasAttemptedAutoOpenRef.current = true;
          return;
        }
        sessionStorage.setItem(latchKey, '1');
      } catch {
        // Restricted webviews may block sessionStorage — still attempt the open once.
      }
    }

    hasAttemptedAutoOpenRef.current = true;

    // Eligibility resolves asynchronously, so the user can already have the
    // panel open by the time it lands. Spending the latch instead of returning
    // before it is what stops the panel reopening itself the instant they close
    // it: `isNotificationsOpen` is a dependency, so an early return here leaves
    // the attempt unspent and the next false re-fires the open. The cards are
    // pinned to the top of a panel that is on screen, which is the outcome the
    // auto-open exists to produce, so the attempt has been served.
    if (isNotificationsOpen) return;

    setNotificationsOpen(true);
  }, [
    enabled,
    hasPinnedNotifications,
    hasQualifiedEngagement,
    isManagedUser,
    isNotificationsOpen,
    isRecordingActive,
    setNotificationsOpen,
  ]);
}
